import { mergeAttributes } from '@tiptap/core'
import type { JsonObject } from '#src/json.ts'
import { resourceBinding } from '#src/resourceBindingRegistry.ts'
import { inlineAtomNode } from '#src/tiptap/nodes/shared.ts'

export interface RegistryVariableAttrs {
  id: string | null
  value: string | null
  resolvedAt: string | null
}

// The text a registry variable shows and serialises: the resolved value, or
// the binding's label while it is still waiting for its context.
export function registryVariableText(attrs: RegistryVariableAttrs): string {
  if (attrs.value !== null) {
    return attrs.value
  }
  return resourceBinding(attrs.id)?.label ?? attrs.id ?? ''
}

const base = inlineAtomNode('registryVariable', 'span', {
  id: { dataAttr: 'data-id' },
  resolvedAt: { dataAttr: 'data-resolved-at' },
})

export const RegistryVariableNode = base.extend({
  addAttributes() {
    const parent = this.parent?.() ?? {}
    return {
      id: parent.id,
      /* Null means deferred. An empty string is a resolved value (the binding
       * had no data), so it is written out whenever it is not null; the
       * factory's truthy check would drop it and the node would resolve again
       * on the next load. */
      value: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-value'),
        renderHTML: (attributes: JsonObject) =>
          typeof attributes.value === 'string'
            ? { 'data-value': attributes.value }
            : {},
      },
      resolvedAt: parent.resolvedAt,
    }
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes({ 'data-type': 'registryVariable' }, HTMLAttributes),
      registryVariableText(node.attrs as RegistryVariableAttrs),
    ]
  },
  renderText({ node }) {
    return registryVariableText(node.attrs as RegistryVariableAttrs)
  },
})
